import { loginRedirect, type Role } from '@svs/nova-core/nextjs/auth';

import { MiddlewarePaymentError } from '../../error/MiddlewarePaymentError.js';
import type { PreMiddlewareFn } from '../../payment/createPayment.js';
import { getReturnUrl } from '../../utils/entry.js';
import { logger } from '../../utils/logger.js';
import type { Wager } from '../../wager/Wager.js';

type PaymentContext = {
  role?: Role;
};

/**
 * Middleware to check the user's session before confirming a payment interaction.
 *
 * @param paymentContext - The context of the payment, which may include details about the payment and user session.
 * @returns The middleware function that checks the user session.
 */
export function authenticateMw(paymentContext: PaymentContext): PreMiddlewareFn<Wager> {
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const fn: PreMiddlewareFn<Wager> = () => (next) => {
    try {
      const { role } = paymentContext;
      if (role !== 'Player' && role !== 'Retailer') {
        // Redirect to deposit page with return URL as query parameter, don't handle action=pay in this version of payment. Implemented in next version of payment.
        const returnUrl = getReturnUrl();
        loginRedirect({ returnUrl: returnUrl.toString(), cancelUrl: returnUrl.toString() });
        next(new MiddlewarePaymentError({ clientCode: 'NOT_AUTHENTICATED' }));
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.debug('Payment aborted', { message: error.message, error });
      }
      const mwError = new MiddlewarePaymentError({
        clientCode: 'NOT_AUTHENTICATED',
      });
      next(mwError);
      return;
    }
    next();
  };

  return fn;
}
